import EventEmitter from "eventemitter3";
import {
  ActionStateEvent,
  ContextUsageEvent,
  InteractionMode,
  isOpenAIRealtimeRuntime,
  ServerConnectOptions,
  StatusEvent,
  SessionConfigEvent,
  SessionEndEvent,
  TextResponseEvent,
  ToolResultEvent,
  ToolStartEvent,
  TranscriptEvent,
  WebSourcesEvent,
} from "../types";

type ClientEvents = {
  open: [];
  close: [CloseEvent | undefined];
  error: [Event];
  audio: [ArrayBuffer];
  interrupted: [];
  sessionresumed: [];
  turncomplete: [];
  inputtranscript: [TranscriptEvent];
  outputtranscript: [TranscriptEvent];
  textresponse: [TextResponseEvent];
  toolstart: [ToolStartEvent];
  toolresult: [ToolResultEvent];
  actionstate: [ActionStateEvent];
  contextusage: [ContextUsageEvent];
  sessionconfig: [SessionConfigEvent];
  sessionend: [SessionEndEvent];
  websources: [WebSourcesEvent];
  status: [StatusEvent];
  assistanttext: [string];
  modechanged: [InteractionMode];
  textrejected: [{ text: string; message: string; artifactIds: string[] }];
};

type ClientStatus = "disconnected" | "connecting" | "connected";

function resolveWsBaseUrl(): string {
  const configured =
    process.env.REACT_APP_SERVER_WS_BASE_URL || window.location.origin;

  if (configured.startsWith("ws://") || configured.startsWith("wss://")) {
    return configured.replace(/\/+$/, "");
  }

  return configured.replace(/^http:/, "ws:").replace(/^https:/, "wss:").replace(/\/+$/, "");
}

function buildUrl(options: ServerConnectOptions): string {
  const url = new URL(`${resolveWsBaseUrl()}/ws`);
  url.searchParams.set("voice", options.voice);
  url.searchParams.set("session_id", options.sessionId);
  if (options.mode) {
    url.searchParams.set("mode", options.mode);
  }
  if (options.startupMode && options.startupMode !== "normal") {
    url.searchParams.set("startup_mode", options.startupMode);
  }
  if (options.forceNew) {
    url.searchParams.set("force_new", "1");
  }
  url.searchParams.set("voice_runtime", options.voiceRuntime || "gemini_live");
  if (isOpenAIRealtimeRuntime(options.voiceRuntime) && options.openAIRealtimeVoice) {
    url.searchParams.set("openai_voice", options.openAIRealtimeVoice);
  }
  if (options.reasoningEffort) {
    url.searchParams.set("reasoning_effort", options.reasoningEffort);
  }
  return url.toString();
}

export class ServerVoiceClient extends EventEmitter<ClientEvents> {
  socket: WebSocket | null = null;
  status: ClientStatus = "disconnected";

  async connect(options: ServerConnectOptions): Promise<boolean> {
    if (this.socket && (this.status === "connecting" || this.status === "connected")) {
      this.disconnect();
    }

    this.status = "connecting";

    return new Promise<boolean>((resolve, reject) => {
      const socket = new WebSocket(buildUrl(options));
      this.socket = socket;

      socket.binaryType = "arraybuffer";

      const cleanup = () => {
        socket.removeEventListener("open", handleOpen);
        socket.removeEventListener("close", handleClose);
        socket.removeEventListener("error", handleError);
        socket.removeEventListener("message", handleMessage);
      };

      const handleOpen = () => {
        if (this.socket !== socket) {
          socket.close(1000, "superseded connection");
          return;
        }
        this.status = "connected";
        this.emit("open");
        resolve(true);
      };

      const handleClose = (event: CloseEvent) => {
        cleanup();
        if (this.socket !== socket) {
          return;
        }
        this.socket = null;
        this.status = "disconnected";
        this.emit("close", event);
        if (event.code !== 1000 && event.code !== 1001) {
          reject(new Error(event.reason || `WebSocket closed with code ${event.code}`));
        }
      };

      const handleError = (event: Event) => {
        if (this.socket !== socket) {
          return;
        }
        this.emit("error", event);
      };

      const handleMessage = (event: MessageEvent) => {
        if (this.socket !== socket) {
          return;
        }
        const raw = typeof event.data === "string" ? event.data : "";
        if (!raw) {
          return;
        }

        let message: any;
        try {
          message = JSON.parse(raw);
        } catch (_error) {
          return;
        }

        switch (message.type) {
          case "status":
            this.emit("status", { message: message.message || "" });
            break;
          case "audio":
            if (typeof message.data === "string") {
              this.emit("audio", base64ToArrayBuffer(message.data));
            }
            break;
          case "input_transcript":
            this.emit("inputtranscript", {
              text: message.text || "",
              finished: Boolean(message.finished),
            });
            break;
          case "output_transcript":
            this.emit("outputtranscript", {
              text: message.text || "",
              finished: Boolean(message.finished),
            });
            if (typeof message.text === "string" && message.text.trim()) {
              this.emit("assistanttext", message.text);
            }
            break;
          case "tool_start":
            this.emit("toolstart", {
              callId: message.callId,
              blocking: message.blocking === true,
              name: message.name || "unknown_tool",
              args: message.args,
            });
            break;
          case "tool_result": {
            const failedToolStatuses = new Set([
              "error",
              "no_results",
              "unavailable",
              "rate_limited",
              "timeout",
            ]);
            const toolFailed = failedToolStatuses.has(message.status);
            this.emit("toolresult", {
              callId: message.callId,
              name: message.name || "unknown_tool",
              status: message.status === "cancelled" ? "cancelled" : toolFailed ? "error" : "ok",
              error: message.error || (toolFailed ? message.summary : undefined),
              summary: message.summary,
              result: message.result,
            });
            break;
          }
          case "text_response":
            this.emit("textresponse", {
              text: message.text || "",
              finished: Boolean(message.finished),
            });
            if (typeof message.text === "string" && message.text.trim()) {
              this.emit("assistanttext", message.text);
            }
            break;
          case "mode_changed":
            this.emit("modechanged", message.mode || "voice");
            break;
          case "context_usage":
            this.emit("contextusage", {
              source: message.source === "estimate" ? "estimate" : "gemini",
              promptTokenCount: toOptionalNumber(message.promptTokenCount),
              responseTokenCount: toOptionalNumber(message.responseTokenCount),
              totalTokenCount: toRequiredNumber(message.totalTokenCount),
              contextWindowTokens: toRequiredNumber(message.contextWindowTokens),
              compressionTriggerTokens: toRequiredNumber(message.compressionTriggerTokens),
              percentUsed: toRequiredNumber(message.percentUsed),
            });
            break;
          case "action_state":
            if (
              message.state === "idle" ||
              message.state === "awaiting_confirmation" ||
              message.state === "executing" ||
              message.state === "executed" ||
              message.state === "canceled" ||
              message.state === "outcome_unknown" ||
              message.state === "inspected"
            ) {
              this.emit("actionstate", {
                state: message.state,
                action: message.action,
                message: message.message,
                confirmationPrompt: message.confirmation_prompt,
                confirmationDetails: message.confirmation_details,
              });
            }
            break;
          case "session_config":
            this.emit("sessionconfig", {
              voiceRuntime: isOpenAIRealtimeRuntime(message.voiceRuntime) ? message.voiceRuntime : "gemini_live",
              modelId: message.modelId || null,
              reasoningEffort: message.reasoningEffort || null,
            });
            break;
          case "web_sources":
            this.emit("websources", {
              status: message.status || "error",
              query: message.query || "",
              answer: message.answer || null,
              message: message.message || null,
              sources: Array.isArray(message.sources) ? message.sources : [],
            });
            break;
          case "session_resumed":
            this.emit("sessionresumed");
            break;
          case "turn_complete":
            this.emit("turncomplete");
            break;
          case "session_end":
            if (
              message.state === "wrapping_up" ||
              message.state === "saving" ||
              message.state === "saved" ||
              message.state === "save_failed"
            ) {
              this.emit("sessionend", {
                state: message.state,
                message: typeof message.message === "string" ? message.message : "",
              });
            }
            break;
          case "interrupted":
            this.emit("interrupted");
            break;
          case "error":
            this.emit("status", { message: message.message || "error" });
            break;
          case "text_rejected":
            this.emit("textrejected", {
              text: typeof message.text === "string" ? message.text : "",
              message: typeof message.message === "string" ? message.message : "Text was rejected",
              artifactIds: Array.isArray(message.artifact_ids)
                ? message.artifact_ids.filter((value: unknown) => typeof value === "string")
                : [],
            });
            break;
          default:
            break;
        }
      };

      socket.addEventListener("open", handleOpen);
      socket.addEventListener("close", handleClose);
      socket.addEventListener("error", handleError);
      socket.addEventListener("message", handleMessage);
    });
  }

  disconnect() {
    if (!this.socket) {
      this.status = "disconnected";
      return;
    }

    const socket = this.socket;
    this.socket = null;
    this.status = "disconnected";
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close(1000, "client disconnect");
    }
  }

  sendAudio(data: string) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({ type: "audio", data }));
  }

  sendAudioStreamEnd() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({ type: "audio_end" }));
  }

  sendPlaybackReady() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({ type: "playback_ready" }));
  }

  sendAudioRouteReady() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({ type: "audio_route_ready" }));
  }

  sendCarStartRequested() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({ type: "car_start_requested" }));
  }

  sendSessionEndRetry() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({ type: "session_end_retry" }));
  }

  sendText(text: string, artifactIds: string[] = []): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    this.socket.send(JSON.stringify({ type: "text", text, artifact_ids: artifactIds }));
    return true;
  }

  sendModeSwitch(mode: InteractionMode) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({ type: "mode_switch", mode }));
  }
}

function toOptionalNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toRequiredNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}
